import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { trackAddToCart } from "@/lib/analytics";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const keyOf = (id, variant, customization) => `${id}::${variant || ""}::${customization || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("ping_cart")) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem("ping_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (product, quantity = 1, variant = null, customization = null, priceOverride = null) => {
    setItems((prev) => {
      const k = keyOf(product.id, variant, customization);
      const existing = prev.find((i) => keyOf(i.product_id, i.variant, i.customization) === k);
      if (existing) {
        return prev.map((i) =>
          keyOf(i.product_id, i.variant, i.customization) === k ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      const unitPrice = priceOverride != null ? priceOverride : product.price;
      return [
        ...prev,
        { product_id: product.id, name: product.name, price: unitPrice, image: product.image, quantity, variant, customization },
      ];
    });
    trackAddToCart(product, quantity);
    toast.success("Added to bag", { description: variant ? `${product.name} · ${variant}` : product.name });
  };

  const updateQty = (key, quantity) => {
    if (quantity < 1) return;
    setItems((prev) => prev.map((i) => (keyOf(i.product_id, i.variant, i.customization) === key ? { ...i, quantity } : i)));
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => keyOf(i.product_id, i.variant, i.customization) !== key));
  };

  const clearCart = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, removeItem, clearCart, count, subtotal, keyOf }}>
      {children}
    </CartContext.Provider>
  );
};
